import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { withoutAppCheck } from 'src/app/core/interceptors/auth.interceptor';
import { withSilentForbidden } from 'src/app/core/interceptors/error.interceptor';
import { environment } from 'src/environments/environment';

/** Partner con el que se está dando de alta una empresa. */
export interface SignupPartner {
  slug: string;
  name: string;
  /** Logo para la marca compartida «WAIIS x Partner»; vacío si no se conoce. */
  logo: string;
  /**
   * Aún esperando el branding del backend (y la descarga del logo): nombre y
   * logo vacíos. Las pantallas reservan el hueco en vez de pintar nada
   * provisional.
   */
  loading?: boolean;
}

/**
 * Clave en la que versiones anteriores guardaban el partner del alta para
 * mandarlo al crear la empresa. Ya no se usa (R5, #20261023-090006): se borra al
 * arrancar para que ninguna alta lleve un partner.
 */
const STORAGE_KEY = 'waiis.signup.partner';

/** Formato de slug: minúsculas, números y guiones, sin guion al principio. */
const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]*$/;

/**
 * Marca de un partner en las pantallas de acceso («WAIIS × JoinUp»), a partir de
 * `GET /api/partners/{slug}/branding`. Hoy la pide «Tu espacio ya está listo»
 * (`/auth/space-ready?partner=<slug>`, el enlace del correo J3).
 *
 * Solo pinta: no hay enlace de partner en el alta (R5, #20261023-090006) y nada
 * se guarda ni se manda al crear la empresa. `/partner/<slug>` redirige a la
 * entrada normal. El partner de grandes cuentas lo decide el backend.
 */
@Injectable({ providedIn: 'root' })
export class PartnerSignupService {
  /** `GET /api/partners/{slug}/branding`: público, devuelve nombre y logo. */
  private readonly API = `${environment.laravelApi}/partners`;

  private readonly entryPartner$ = new BehaviorSubject<SignupPartner | null>(null);

  /** Partner de entrada, que se actualiza cuando llega el branding. */
  readonly partner$ = this.entryPartner$.asObservable();

  constructor(private http: HttpClient) {
    // Lo crea `AppRoutingModule` al arrancar: limpia el partner que dejaron
    // guardado versiones anteriores.
    this.clear();
  }

  /**
   * Fija el partner de entrada a partir de la URL:
   * - con slug válido (`/partner/<slug>`), se pide su branding al backend y se
   *   descarga su logo antes de darlo por cargado (mientras, `loading`);
   * - sin slug, o con uno que no cumple el formato, no hay partner de entrada y
   *   NO se llama al backend.
   *
   * La carga sigue aunque nadie se suscriba (o el resolver deje de esperar): el
   * observable que devuelve solo avisa de cuándo ha terminado. Si ya es el
   * partner de entrada (el resolver de la ruta lo ha pedido), no se repite.
   *
   * No toca `localStorage`: eso solo pasa al iniciar sesión o registrarse.
   */
  resolve(slug: string | null | undefined): Observable<SignupPartner | null> {
    const clean = this.cleanSlug(slug);
    if (!clean) {
      this.entryPartner$.next(null);
      return of(null);
    }
    if (this.partner?.slug === clean) return this.settled$();

    this.entryPartner$.next({ slug: clean, name: '', logo: '', loading: true });

    this.http
      .get<any>(`${this.API}/${encodeURIComponent(clean)}/branding`, {
        // Pública: no espera al token de App Check, que tarda en llegar.
        context: withSilentForbidden(withoutAppCheck()),
      })
      .pipe(
        map((res): SignupPartner | null =>
          this.mapPartner(clean, res?.data ?? res),
        ),
        catchError((error: HttpErrorResponse) =>
          // 404: ese partner no existe, así que no hay con quién vincular.
          // Cualquier otro fallo mantiene el slug, con él mismo como nombre.
          of(error?.status === 404 ? null : this.mapPartner(clean, null)),
        ),
        // El logo se descarga antes de publicarlo, para que se pinte de golpe.
        switchMap((partner) =>
          partner?.logo ? preloadImage(partner.logo).pipe(map(() => partner)) : of(partner),
        ),
      )
      .subscribe((partner) => {
        // Solo si sigue siendo la misma entrada (no se ha ido a otro link).
        if (this.partner?.slug === clean) this.entryPartner$.next(partner);
      });

    return this.settled$();
  }

  /** Emite una vez, cuando el partner de entrada ya no está cargando. */
  private settled$(): Observable<SignupPartner | null> {
    return this.partner$.pipe(
      filter((partner) => !partner?.loading),
      take(1),
    );
  }

  /**
   * Partner de la entrada actual, para la marca de las pantallas de alta; `null`
   * si no se ha llegado desde `/partner/<slug>`.
   */
  get partner(): SignupPartner | null {
    return this.entryPartner$.value;
  }

  /**
   * Slug para el alta de la empresa: siempre `null`, ya no hay enlace de
   * partner (R5). Se mantiene para el asistente antiguo de «Primeros pasos»,
   * que se retira en #20261023-090030.
   */
  get slug(): string | null {
    return null;
  }

  /**
   * Se llama al completar un inicio de sesión o un registro: la marca de la
   * pantalla de acceso no sigue a la sesión y no se guarda ningún partner.
   */
  commitOnAuth(): void {
    this.clear();
    this.entryPartner$.next(null);
  }

  /** Borra el partner que guardaban versiones anteriores. */
  clear(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Modo incógnito o almacenamiento bloqueado: el alta sigue, sin partner.
    }
  }

  /** Sin datos del backend, el slug hace de nombre y no hay logo. */
  private mapPartner(slug: string, data: any): SignupPartner {
    return {
      slug: data?.slug || slug,
      name: data?.name || slug,
      logo: data?.logo_url || data?.logo || '',
    };
  }

  /** Slug normalizado, o cadena vacía si no cumple el formato. */
  private cleanSlug(slug: string | null | undefined): string {
    const clean = (slug || '').trim().toLowerCase();
    return SLUG_PATTERN.test(clean) ? clean : '';
  }
}

/**
 * Descarga una imagen y avisa al terminar (también si falla: entonces se pinta
 * el `alt`). Queda en la caché del navegador, así que el `<img>` sale al momento.
 */
function preloadImage(url: string): Observable<void> {
  return new Observable<void>((subscriber) => {
    const img = new Image();
    img.onload = img.onerror = () => {
      subscriber.next();
      subscriber.complete();
    };
    img.src = url;
    return () => {
      img.onload = img.onerror = null;
    };
  });
}
